import { useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ReactQueryDevtools } from '@zg-rentals/react-query5';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { StickyContainer } from 'react-sticky';

import { AppContainer } from '@zg-rentals/rental-manager-env';
import { ResponsiveHeading } from '@zillow/zrs-comet';
import { RentalsB2BLayout } from '@zg-rentals/b2b-layout';

import { ToastProvider } from '@zillow/constellation';
import { Banner, Box, CloseButton, ToastProvider as ToastProvider10 } from '@zillow/constellation-10';
import { parsePathAndQueryParams, parseQueryString } from '@zg-rentals/string-utils';
import { SplitProvider, useList } from '@zg-rentals/split';

import { throttle } from '@zg-rentals/zdash';
import { BlockedUserModal } from '@zg-rentals/blocked-user-modal';
import { RequestWidget } from '@zg-rentals/rental-manager-env';
import { useSiftScience } from '@zg-rentals/sift-science';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { logError } from '@zg-rentals/log-error';

import { useBuildNumber } from '../../hooks/queries/app';

import CurrentProperty from '../../model/CurrentProperty';

import { LoadingMapContext } from '../../providers/LoadingMapContext/context';
import { ModularListingContext } from '../CreateListingPage/context';
import { DashboardContext } from '../DashboardPage/context';
import { FreemiumContext } from '../../components/Freemium/context';
import { PaymentGatewayContext } from '../../components/PaymentGateway/context';
import { WizardContext } from '../../components/Wizard/context';

import {
  INIT_LOADING_STATE as INIT_LOADING_MAP_STATE,
  LOADING_KEYS_MAP,
  loadingMapReducer,
} from '../../providers/LoadingMapContext/reducer';

import modularListingReducer, { INIT_STATE as INIT_MODULAR_LISTING_STATE } from '../CreateListingPage/reducer';
import dashboardReducer, { INIT_STATE as INIT_DASHBOARD_STATE } from '../DashboardPage/reducer';
import paymentGatewayReducer, { INIT_STATE as INIT_PG_STATE } from '../../components/PaymentGateway/reducer';
import freemiumReducer, { INIT_FREEMIUM_STATE } from '../../components/Freemium/reducer';
import wizardReducer, { INIT_STATE as WIZARD_INIT_STATE } from '../../components/Wizard/reducer';

import PaymentGateway from '../../components/PaymentGateway';
import { ToastRenderer } from '../../components/ToastRenderer';
import PostActivationDashboardModal from '../../components/Modal/PostActivationDashboardModal';
import { RMWContext } from '../../providers/RMWContext';
import cacheStorageUtils from '../../../client/workers/utils/cacheStorage';
import Routes from '../../routes';
import accessUtils from '../../utils/accessUtils';
import * as app from '../../slices/app';
import PropertiesActions from '../../actions/PropertiesActions';
import UserActions from '../../actions/UserActions';
import queryUtils from '../../utils/queryUtils';
import { AppDownloadUpsellBanner } from '@zg-rentals/app-download-upsell-banner';
import { AppSelectors, PropertiesSelectors, RoutingSelectors, UserSelectors } from '../../selectors';
import EnvSelectors from '../../selectors/EnvSelectors';
import { RMW_SUB_PATH } from '../../pathsManifest';

import '../../../index.css';
import { trackComscoreEvent, trackEvent } from '../../../client/analytics';

const SplitHook = () => {
  useList();

  return null;
};

const AppVersionControlHook = () => {
  useBuildNumber();

  return null;
};

const App = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { paths, isInMobileWebView, brand } = useContext(RMWContext);

  const prevPath = useRef('');

  const [modularListingState, modularListingReducerDispatch] = useReducer(
    modularListingReducer,
    INIT_MODULAR_LISTING_STATE,
  );

  const [dashboardState, dashboardReducerDispatch] = useReducer(dashboardReducer, INIT_DASHBOARD_STATE);

  const [freemiumState, freemiumReducerDispatch] = useReducer(freemiumReducer, INIT_FREEMIUM_STATE);
  const [pgState, pgReducerDispatch] = useReducer(paymentGatewayReducer, INIT_PG_STATE);
  const [wizardState, reducerWizardDispatch] = useReducer(wizardReducer, WIZARD_INIT_STATE);
  const [loadingMapState, loadingMapDispatch] = useReducer(loadingMapReducer, INIT_LOADING_MAP_STATE);

  const [displayOfflineNotification, setDisplayOfflineNotification] = useState(false);
  const [displayBlockedUserModal, setDisplayBlockedUserModal] = useState(false);

  const {
    app: { canAccessDashboardRedesign, appConfig },
    properties: {
      currentProperty: { rentalProtection },
      postActivationState: { displayPostActivationModal },
    },
  } = useSelector((state) => state);
  const isWizardView = useSelector((state) => AppSelectors.makeIsWizardView(state));
  const isFooterlessView = useSelector((state) => AppSelectors.makeIsFooterlessView(state));
  const isLoggedIn = useSelector((state) => UserSelectors.makeIsLoggedIn(state));
  const isOnline = useSelector((state) => AppSelectors.makeIsOnline(state));
  const isProduction = useSelector((state) => EnvSelectors.makeIsProduction(state));
  const isRunningInProd = useSelector((state) => AppSelectors.makeIsRunningInProd(state));
  const isUserAllowed = useSelector((state) => UserSelectors.makeIsUserAllowed(state));
  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));
  const pathname = useSelector((state) => RoutingSelectors.makePathname(state));
  const queryString = useSelector((state) => RoutingSelectors.makeSearch(state));
  const user = useSelector((state) => UserSelectors.makeUser(state));
  const currentProperty = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));
  const cookieEnabledServices = useSelector((state) => state.app.cookieEnabledServices);

  const modularListingValue = useMemo(
    () => ({ modularListingState, reducerDispatch: modularListingReducerDispatch }),
    [modularListingState],
  );

  const dashboardValue = useMemo(
    () => ({ dashboardState, reducerDispatch: dashboardReducerDispatch }),
    [dashboardState],
  );

  const pgValue = useMemo(() => ({ pgState, reducerDispatch: pgReducerDispatch }), [pgState]);
  /**
   * We have to use @ts-expect-error on implementation of freemiumReducerDispatch because we are expecting
   * a key of reducerDispatch in the FreemiumContext.Provider value
   *
   * we should follow the same pattern as the other context providers implementation when creating the context values
   *
   * we should use the following:
   * const freemiumValue = useMemo(() => ({ freemiumState, reducerDispatch: freemiumReducerDispatch }), [freemiumState]);
   *
   * will need to do some regression testing upon changing the implementation
   */
  const freemiumValue = useMemo(
    () => ({ freemiumState, freemiumReducerDispatch }),
    [freemiumState, freemiumReducerDispatch],
  );
  const wizardValue = useMemo(() => ({ wizardState, reducerWizardDispatch }), [wizardState]);

  const loadingMapValue = useMemo(() => {
    const isComponentLoadingMap = {};
    Object.entries(LOADING_KEYS_MAP).forEach(([component, keys]) => {
      isComponentLoadingMap[component] = keys.some((key) => loadingMapState[key]);
    });

    return { loadingMapState, reducerDispatch: loadingMapDispatch, loadingByComponent: isComponentLoadingMap };
  }, [loadingMapState, loadingMapDispatch]);

  const {
    email: userEmail,
    impersonatorEmail,
    impersonatorUserToken,
    loggedIn,
    features,
    isEnterpriseCustomer,
    userToken,
  } = user;
  const parsedQueryString = queryString ? parseQueryString(queryString) : {};
  const pathNameWithoutTrailingSlash = pathname.replace(/\/$/, '');
  const isZRMLandingPage = location.pathname === '/rental-manager/';
  const isSubmitRequestPage = location.pathname.includes('/rental-manager/submit-request');
  const isPostAListingPage = pathNameWithoutTrailingSlash === paths.postAListingPage;
  const isDashboardPage = pathNameWithoutTrailingSlash.includes(RMW_SUB_PATH.DASHBOARD);
  const displayFooter = !isInMobileWebView && !isWizardView && !isFooterlessView;

  useSiftScience({ email: userEmail, isImpersonator: Boolean(impersonatorUserToken) });

  const userCanAccessRequestWidget =
    accessUtils.canAccessRequestWidget({ appConfig }) &&
    (user.isMultifamilyCustomer || user.isSingleFamilyEnterpriseCustomer) &&
    !isZRMLandingPage &&
    !isSubmitRequestPage;

  const canAccessB2BLayout = accessUtils.canAccessB2BLayout({ appConfig });

  const hideHeaderNavigation = isInMobileWebView === true || isWizardView === true;

  /**
   *  TODO: implement if we need data set to pass to routes
   *  most likely we would add an AppContext file
   *  ex: appState.fetchingRequiredData
   *
   *  adding this temporarily to see if there is any flickering or out of sync UI while data is being hydrated
   *  if there are no issues we can remove this
   **/
  const fetchingRequiredData = false;

  const isInReactivationFlow = accessUtils.canAccessMLReactivationFlow({
    currentProperty,
  });

  const canAccessPostActivationWorkflow = accessUtils.canAccessPostActivationWorkflow({ appConfig });

  if (__BROWSER__) {
    // expose context states
    window.contextState = {
      modularListingState,
      pgState,
      freemiumState,
      freemiumReducerDispatch,
      wizardState,
      dashboardState,
    };
  }

  const checkSalesReferral = () => {
    const queryParamObj = parseQueryString(queryUtils.cleanQueryParams(queryString));
    const code = queryParamObj.pisrc;

    if (code) {
      dispatch(UserActions.addReferral({ code }))
        .then(() => {
          delete queryParamObj.pisrc;
          const queryString = new URLSearchParams(queryParamObj).toString();
          const tempQueryParams = queryString ? `?${queryString}` : '';
          const locationObj = parsePathAndQueryParams(`${pathname}${tempQueryParams}`);
          const { path, queryParams } = locationObj;
          const fullPath = `${path}${queryParams}`;

          window.router.transitionTo(fullPath);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'App.checkSalesReferral',
          });
        });
    }
  };

  const clearStorage = () => {
    // cache storage will come back on app load if true
    return cacheStorageUtils.clear();
  };

  const handleNotificationCloseClick = () => setDisplayOfflineNotification(false);
  const handleResize = () => dispatch(app.calculateScreenSize());
  const handleOffline = () => dispatch(app.updateIsOnline(false));
  const handleOnline = () => dispatch(app.updateIsOnline(true));
  const handleThrottledResize = throttle(() => handleResize(), 10);

  useZrmPageViewEvents(trackEvent.user, location.pathname, user);
  useComscorePageTracking(() => trackComscoreEvent({}), location.pathname);

  const onRouteChange = () => {
    if (!isUserAllowed) {
      //  redirect and open BlockedUserModal unless on submit request page or feature flags page
      if (
        pathname.toLowerCase().includes(paths.submitRequest.toLowerCase()) ||
        pathname.toLowerCase().includes(paths.featureFlagPage.toLowerCase()) ||
        pathname.toLowerCase().includes(paths.securityInquiry.toLowerCase())
      ) {
        setDisplayBlockedUserModal(false);
      } else {
        setDisplayBlockedUserModal(true);
        history.replace(paths.propertiesPage);
      }
    }

    checkLeadIntentionType();
    prevPath.current = location.pathname;
  };

  const checkLeadIntentionType = () => {
    let intentionType = '';
    if (pathname.includes('inquiry-contact')) {
      intentionType = parsedQueryString.intentionType || '';
    } else if (pathname.includes('deactivate')) {
      intentionType = 'deactivateListing';
    } else if (pathname.includes('applications')) {
      intentionType = 'sendApplicationInvite';
    }

    if (
      parsedQueryString.inquiryId &&
      parsedQueryString.controlHash &&
      intentionType &&
      parsedQueryString.utm_medium &&
      parsedQueryString.utm_medium === 'email'
    ) {
      dispatch(
        UserActions.insertAction({
          intentionType,
          inquiryId: parsedQueryString.inquiryId,
          controlHash: parsedQueryString.controlHash,
          landlordBrand: parsedQueryString.landlordBrand,
          renterBrand: parsedQueryString.renterBrand,
        }),
      );
    }
  };

  const resetCurrentProperty = () => {
    dispatch(PropertiesActions.updateCurrentProperty(CurrentProperty()));
  };

  const updateCanAccessRentGuaranteeFromML = () => {
    const canAccessRentalProtectionFromML = accessUtils.canAccessRentalProtectionFromML({
      appConfig,
      rentalProtection,
    });
    dispatch(app.updateCanAccessRentalProtectionFromML(canAccessRentalProtectionFromML));
  };

  const updateCanAccessDashboardRedesign = () => {
    const canAccessDashboardRedesign = accessUtils.canAccessDashboardRedesign({
      currentProperty,
    });

    dispatch(app.updateCanAccessDashboardRedesign(canAccessDashboardRedesign));
  };

  const updateCanAccessMaintenanceTracker = () => {
    const canAccessMaintenanceTracker = accessUtils.canAccessMaintenanceTracker({
      canAccessMaintenanceTracker: features?.canAccessMaintenanceTracker,
      cookieZGMaintenanceTracker: cookieEnabledServices?.ZRM_MAINTENANCE_TRACKER,
    });

    dispatch(app.updateCanAccessMaintenanceTracker(canAccessMaintenanceTracker));
  };

  const closePostActivationModal = () => {
    dispatch(PropertiesActions.updatePostActivationState({ displayPostActivationModal: false, daysExtended: null }));
  };

  useEffect(() => {
    /**
     * when listingId is undefined we clear currentProperty
     * this handles a race condition where isMultifamily is not updated yet
     **/
    if (!listingId) {
      resetCurrentProperty();
    }
  }, [listingId]);

  useEffect(() => {
    updateCanAccessDashboardRedesign();
  }, [currentProperty?.isMultifamily]);

  useEffect(() => {
    updateCanAccessMaintenanceTracker();
  }, [features?.canAccessMaintenanceTracker]);

  useEffect(() => {
    updateCanAccessRentGuaranteeFromML();
  }, [rentalProtection.isEligible]);

  useEffect(() => {
    if (__BROWSER__) {
      cacheStorageUtils.clearStaleUserSessions(userToken);
    }

    try {
      dispatch(app.updateIsOnline(window.navigator.onLine));
      checkSalesReferral();
      handleResize();
    } catch (error) {
      logError({ error, context: 'App.componentDidMount' });
    }

    window.addEventListener('resize', handleThrottledResize);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('resize', handleThrottledResize);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    onRouteChange();
  }, [location.pathname]);
  useEffect(() => {
    setDisplayOfflineNotification(!isOnline);
  }, [isOnline]);
  useEffect(() => {
    if (currentProperty.listingAlias) {
      updateCanAccessRentGuaranteeFromML();
    }
  }, [currentProperty.listingAlias]);

  useEffect(() => {
    if (!isLoggedIn) {
      clearStorage();
    }
  }, [isLoggedIn]);

  // If session expired from 30 minutes of inactivity, while impersonating, we need
  // to redirect user to the login page
  // Locally, this will cause an infinite redirect, so only doing this on __PROD__
  if (isProduction && impersonatorEmail && impersonatorUserToken && !loggedIn) {
    return <Redirect to={paths.signInPage(location.pathname)} />;
  }

  return (
    <SplitProvider>
      <ToastProvider placement="bottom-end" duration={3000}>
        <ToastProvider10.Root>
          <WizardContext.Provider value={wizardValue}>
            <ModularListingContext.Provider value={modularListingValue}>
              <LoadingMapContext.Provider value={loadingMapValue}>
                <DashboardContext.Provider value={dashboardValue}>
                  <PaymentGatewayContext.Provider value={pgValue}>
                    <FreemiumContext.Provider value={freemiumValue}>
                      {__DEV__ && <SplitHook />}

                      {!__DEV__ && <AppVersionControlHook />}

                      <div id="ref-hook" />

                      <StickyContainer>
                        <Box
                          css={{ width: '100%', position: 'relative', height: '100%', overflow: 'hidden' }}
                          data-test="App"
                        >
                          <div>
                            {displayOfflineNotification && (
                              <Banner
                                tone="warning"
                                closeButton={<CloseButton onImpact />}
                                onClose={handleNotificationCloseClick}
                              >
                                You are browsing offline. Please check your internet connection.
                              </Banner>
                            )}
                            <AppDownloadUpsellBanner />
                            {isPostAListingPage && canAccessB2BLayout ? (
                              <RentalsB2BLayout
                                router={{
                                  asPath: pathname + queryString,
                                  pathname: pathNameWithoutTrailingSlash,
                                }}
                              >
                                <Routes
                                  brand={brand}
                                  canAccessDashboardRedesign={canAccessDashboardRedesign}
                                  isRunningInProd={isRunningInProd}
                                  pathname={pathname}
                                  isInReactivationFlow={isInReactivationFlow}
                                />
                              </RentalsB2BLayout>
                            ) : (
                              <AppContainer
                                isLoading={fetchingRequiredData}
                                escapeAllLinks={false}
                                hideHeader={hideHeaderNavigation}
                                userModel={user}
                                hideFooter={!displayFooter}
                                isInMobileWebView={isInMobileWebView}
                              >
                                <Routes
                                  brand={brand}
                                  canAccessDashboardRedesign={canAccessDashboardRedesign}
                                  isRunningInProd={isRunningInProd}
                                  pathname={pathname}
                                  isInReactivationFlow={isInReactivationFlow}
                                />
                              </AppContainer>
                            )}
                            <ToastProvider10.Portal>
                              <ToastProvider10.Content>
                                <ToastRenderer />
                              </ToastProvider10.Content>
                            </ToastProvider10.Portal>
                          </div>

                          {userCanAccessRequestWidget && <RequestWidget zrmwUser={user} />}
                          {displayBlockedUserModal && <BlockedUserModal restrictions={user?.restrictions} />}
                          {canAccessPostActivationWorkflow && displayPostActivationModal && !fetchingRequiredData && (
                            <PostActivationDashboardModal
                              isOpen={displayPostActivationModal && isDashboardPage}
                              onClose={closePostActivationModal}
                            />
                          )}

                          {/* <PostActivationDashboardModal isOpen={true} onClose={closePostActivationModal} /> */}

                          <PaymentGateway displayPaymentGateway={pgState?.displayPaymentGateway} />

                          <ResponsiveHeading />
                        </Box>
                      </StickyContainer>
                    </FreemiumContext.Provider>
                  </PaymentGatewayContext.Provider>
                </DashboardContext.Provider>
              </LoadingMapContext.Provider>
            </ModularListingContext.Provider>
          </WizardContext.Provider>
        </ToastProvider10.Root>
      </ToastProvider>
      {cookieEnabledServices.ZRM_REACT_QUERY_DEVTOOLS && <ReactQueryDevtools />}
    </SplitProvider>
  );
};

export { App };
export default App;
